import type { CompanyTree } from "@rakazo/contracts";
import { Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import { Check, Plus } from "lucide-react";
import { createContext, useState } from "react";
import { IconTooltip } from "../../components/IconTooltip";
import { CompanyAvatar } from "./company-avatar";

// The roster stays mounted while another section owns the content pane, so its
// company controls can stay in the rail without a second copy of company state.
export const CompanyRailContext = createContext<{
  switcherHost: HTMLDivElement | null;
  headingHost: HTMLDivElement | null;
  onCreateCompany: () => void;
} | null>(null);

export function CompanySwitcher({
  companies,
  selected,
  onSelect,
  onCreate,
  canSelect,
}: {
  companies: CompanyTree;
  selected: CompanyTree[number];
  onSelect: (company: CompanyTree[number]) => void;
  onCreate?: () => void;
  canSelect: (company: CompanyTree[number]) => boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <IconTooltip label={`Switch company · ${selected.name}`}>
        <PopoverTrigger
          aria-label={`Switch company: ${selected.name}`}
          data-testid="company-switcher"
          className="app-no-drag flex size-12 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-foreground shadow-sm outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
        >
          <CompanyAvatar value={selected.avatar} className="size-9" />
        </PopoverTrigger>
      </IconTooltip>
      <PopoverContent
        side="inline-end"
        align="start"
        sideOffset={12}
        className="w-72 max-w-[calc(100vw-24px)] gap-1 text-base"
      >
        <p className="px-3 py-2 font-semibold">Companies</p>
        <div className="max-h-[60vh] overflow-y-auto">
          {companies.map((company) => (
            <button
              key={company.id}
              type="button"
              disabled={!canSelect(company)}
              aria-pressed={company.id === selected.id}
              onClick={() => {
                setOpen(false);
                onSelect(company);
              }}
              className="flex min-h-12 w-full items-center gap-3 rounded-lg px-3 text-start hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <CompanyAvatar value={company.avatar} className="size-8 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{company.name}</span>
              {company.id === selected.id ? (
                <Check className="size-4 shrink-0" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </div>
        {onCreate ? (
          <button
            type="button"
            className="mt-1 flex min-h-11 items-center gap-3 rounded-lg border-t border-border px-3 text-start hover:bg-accent"
            onClick={() => {
              setOpen(false);
              onCreate();
            }}
          >
            <Plus className="size-5" aria-hidden="true" />
            New company
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
